import React from 'react';
import { Pencil, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import { Spinner } from '@crc-evans/ui-components';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';
import {
  deliveryStatusFromOnTimeFlag,
  formatDateDisplay,
  formatPercent,
  isNegativeHighlight,
} from '../../utils/fabricationJobCalculations';
import '../../pages/technical/HeatProfilePage.css';
import './FabricationDashboard.css';

const COLUMNS = [
  { key: 'projectNo', label: 'Project No', left: true, facet: true },
  { key: 'client', label: 'Client', left: true, facet: true },
  { key: 'sector', label: 'Sector', left: true, facet: true },
  { key: 'promisedMonth', label: 'Promised Month', left: true },
  { key: 'quotedHours', label: 'Quoted Hours', num: true },
  { key: 'actualHours', label: 'Actual Hours', num: true },
  { key: 'hoursVariance', label: 'Hours Variance', num: true, derived: true },
  { key: 'hrsVariancePct', label: 'Hrs Variance %', num: true, derived: true, pct: true },
  { key: 'deliveryStatus', label: 'Delivery Status', left: true, facet: true },
  { key: 'site', label: 'Site', left: true, facet: true },
  { key: 'promisedDeliveryDate', label: 'Promised Delivery Date', date: true },
  { key: 'actualDeliveryDate', label: 'Actual Delivery Date', date: true },
  { key: 'deliveryDaysVariance', label: 'Delivery Days Variance', num: true, derived: true },
  { key: 'totalWeldedJoints', label: 'Total Welded Joints', num: true },
  { key: 'totalJointsRejected', label: 'Total Joints Rejected', num: true },
  { key: 'rejectRatePct', label: 'Reject Rate %', num: true, derived: true, pct: true },
];

const formatCell = (row, col) => {
  if (col.key === 'deliveryStatus') {
    const fromFlag = deliveryStatusFromOnTimeFlag(
      row.onTimeFlag != null && row.onTimeFlag !== '' ? Number(row.onTimeFlag) : null
    );
    if (fromFlag) return fromFlag;
  }
  const v = row[col.key];
  if (col.date) return formatDateDisplay(v);
  if (col.pct) return formatPercent(v, 0);
  if (col.num && v != null && v !== '') return String(v);
  if (v == null || v === '') return '—';
  return String(v);
};

const cellClass = (col, row) => {
  if (!col.derived) return '';
  if (col.key === 'hrsVariancePct' && isNegativeHighlight('hrsVariancePct', row.hrsVariancePct)) {
    return 'fabrication-cell--negative';
  }
  if (col.key === 'deliveryDaysVariance' && isNegativeHighlight('deliveryDaysVariance', row.deliveryDaysVariance)) {
    return 'fabrication-cell--negative';
  }
  return '';
};

const FabricationModernDataGrid = ({
  sortedRows,
  isLoading,
  current,
  pageSize,
  sortKey,
  sortDir,
  onToggleSort,
  columnFilters,
  onColumnFilterChange,
  renderTextColumnFilterTrigger,
  onEdit,
  onDelete,
  readOnly,
}) => {
  const renderSortableHead = (col) => {
    const active = sortKey === col.key;
    const Icon = active && sortDir === 'desc' ? ChevronDown : ChevronUp;
    const filterNode =
      col.facet && typeof renderTextColumnFilterTrigger === 'function'
        ? renderTextColumnFilterTrigger(col.key)
        : null;
    return (
      <div
        className={`fabrication-column-head ${
          col.left ? 'fabrication-column-head--left' : ''
        }`}
      >
        <div
          className="fabrication-sortable"
          role="button"
          tabIndex={0}
          onClick={() => onToggleSort(col.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleSort(col.key);
            }
          }}
        >
          <span>{col.label}</span>
          <Icon
            className={`fabrication-sort-icon ${active ? 'fabrication-sort-icon-active' : ''}`}
            size={14}
            aria-hidden
          />
        </div>
        {filterNode}
      </div>
    );
  };

  const colFilter = (col) => (
    <input
      type="text"
      className="fabrication-col-filter"
      placeholder="Search..."
      value={columnFilters[col.key] ?? ''}
      onChange={(e) => onColumnFilterChange(col.key, e.target.value)}
      aria-label={`Filter ${col.label}`}
    />
  );

  const filterSpacer = <div className="fabrication-col-filter-spacer" aria-hidden />;

  return (
    <div className="fabrication-table-shell">
      <div className="fabrication-hscroll">
        <table className="fabrication-grid-table" cellPadding={0} cellSpacing={0}>
          <colgroup>
            <col className="fabrication-col fabrication-col--index" />
            {COLUMNS.map((col) => (
              <col key={col.key} className={`fabrication-col fabrication-col--${col.key}`} />
            ))}
            <col className="fabrication-col fabrication-col--actions" />
          </colgroup>
          <thead className="fabrication-thead">
            <tr className="fabrication-header-row fabrication-header-row--labels">
              <th scope="col" className="fabrication-cell fabrication-cell--num">
                #
              </th>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={`fabrication-cell ${
                    col.left ? 'fabrication-cell--left' : ''
                  } ${col.num ? 'fabrication-cell--num' : ''}`}
                >
                  {renderSortableHead(col)}
                </th>
              ))}
              <th scope="col" className="fabrication-cell fabrication-cell-actions">
                Actions
              </th>
            </tr>
            <tr className="fabrication-header-row fabrication-header-row--filters">
              <th className="fabrication-cell fabrication-cell--num">{filterSpacer}</th>
              {COLUMNS.map((col) => (
                <th
                  key={`f-${col.key}`}
                  className={`fabrication-cell ${col.left ? 'fabrication-cell--left' : ''} ${
                    col.num ? 'fabrication-cell--num' : ''
                  }`}
                >
                  {colFilter(col)}
                </th>
              ))}
              <th
                className="fabrication-cell fabrication-cell-actions fabrication-filter-cell--no-input"
                aria-hidden
              />
            </tr>
          </thead>
          <tbody className="fabrication-body-scroll">
            {isLoading ? (
              <tr className="fabrication-status-row">
                <td colSpan={COLUMNS.length + 2} className="fabrication-loading-cell">
                  <div className="fabrication-loading">
                    <Spinner size="medium" />
                  </div>
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr className="fabrication-status-row">
                <td colSpan={COLUMNS.length + 2} className="fabrication-empty-cell">
                  <div className="fabrication-empty">
                    <ProjectTabTableEmpty />
                  </div>
                </td>
              </tr>
            ) : (
              sortedRows.map((row, index) => (
                <tr key={row.id ?? `${row.projectNo}-${index}`} className="fabrication-row">
                  <td className="fabrication-cell fabrication-cell--num">
                    {(current - 1) * pageSize + index + 1}
                  </td>
                  {COLUMNS.map((col) => (
                    <td
                      key={col.key}
                      className={`fabrication-cell ${col.left ? 'fabrication-cell--left' : ''} ${
                        col.num ? 'fabrication-cell--num' : ''
                      } ${cellClass(col, row)}`}
                      title={formatCell(row, col)}
                    >
                      {formatCell(row, col)}
                    </td>
                  ))}
                  <td className="fabrication-cell fabrication-cell-actions">
                    <button
                      type="button"
                      className="fabrication-action-btn"
                      onClick={() => onEdit(row)}
                      title="Edit"
                      aria-label="Edit fabrication job"
                      disabled={readOnly}
                    >
                      <Pencil size={18} strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      className="fabrication-action-btn fabrication-action-delete"
                      onClick={() => onDelete(row)}
                      title="Delete"
                      aria-label="Delete fabrication job"
                      disabled={readOnly}
                    >
                      <Trash2 size={18} strokeWidth={1.75} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FabricationModernDataGrid;
export { COLUMNS as FABRICATION_GRID_COLUMNS };
